import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { executeAiAction, fetchAiActionPlan, fetchAiSearch } from '../../lib/api';
import type { AiActionItem } from '../../lib/types';
import { safeString } from '../../lib/format';
import { ALL_NAVIGABLE } from '../../lib/navRegistry';
import { useUiStore, type ViewStateKey } from '../../store/ui';
import { toast } from './Toast';

const ITEMS = ALL_NAVIGABLE.map((entry) => ({
  label: entry.label,
  path: entry.to,
  icon: entry.icon,
  keywords: entry.keywords,
}));

function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function actionSearchText(item: AiActionItem): string {
  return normalize(
    [
      safeString(item.title),
      safeString(item.reason),
      safeString(item.explanation),
      safeString(item.category),
      safeString(item.action_type),
      safeString(item.target?.target_tab),
    ]
      .filter(Boolean)
      .join(' '),
  );
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const [aiSearching, setAiSearching] = useState(false);
  const [aiResult, setAiResult] = useState<{ route: string; explanation: string } | null>(null);
  const [aiActions, setAiActions] = useState<AiActionItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const selectedYear = useUiStore((state) => state.selectedYear);
  const replaceViewState = useUiStore((state) => state.replaceViewState);
  const queryClient = useQueryClient();

  const isAiQuery = query.startsWith('?');
  const isAiActionQuery = query.startsWith('!');

  const navigationItems = useMemo(() => {
    if (isAiQuery || isAiActionQuery) return [];
    if (!query.trim()) return ITEMS;
    const needle = normalize(query);
    return ITEMS.filter((item) => normalize(`${item.label} ${item.keywords}`).includes(needle));
  }, [isAiActionQuery, isAiQuery, query]);

  useEffect(() => {
    setSelected(0);
  }, [query, aiActions.length, aiResult?.route, navigationItems.length]);

  useEffect(() => {
    if (!isAiQuery) setAiResult(null);
    if (!isAiActionQuery) setAiActions([]);
  }, [isAiActionQuery, isAiQuery]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setOpen((current) => {
          if (current) return false;
          setQuery('');
          setSelected(0);
          setAiResult(null);
          setAiActions([]);
          return true;
        });
      }
      if (event.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const go = (path: string, view?: string, filters?: Record<string, unknown>) => {
    if (view) {
      replaceViewState(view as ViewStateKey, (filters || {}) as Record<string, string>);
    }
    navigate(path);
    setOpen(false);
    setQuery('');
    setAiResult(null);
    setAiActions([]);
  };

  const invalidateAiTabs = async (tabs: string[]) => {
    await Promise.all(
      tabs.map((tab) => {
        if (tab === 'dashboard') return queryClient.invalidateQueries({ queryKey: ['dashboard', selectedYear] });
        if (tab === 'finance_ops') return queryClient.invalidateQueries({ queryKey: ['finance-ops', selectedYear] });
        if (tab === 'data_quality') return queryClient.invalidateQueries({ queryKey: ['data-quality', selectedYear] });
        if (tab === 'ai_insights') return queryClient.invalidateQueries({ queryKey: ['ai-insights', selectedYear] });
        if (tab === 'integrations') return queryClient.invalidateQueries({ queryKey: ['integrations'] });
        if (tab === 'notifications') return queryClient.invalidateQueries({ queryKey: ['notifications'] });
        return queryClient.invalidateQueries({ queryKey: ['tab', tab, selectedYear] });
      }),
    );
    await queryClient.invalidateQueries({ queryKey: ['ai-actions', selectedYear] });
  };

  const runAiSearch = async () => {
    const q = query.slice(1).trim();
    if (!q) return;
    setAiSearching(true);
    setAiActions([]);
    try {
      const result = await fetchAiSearch(q, selectedYear);
      const route = safeString(result.route);
      if (result.ok && route) {
        setAiResult({ route, explanation: safeString(result.explanation) || 'Ouvrir la vue suggeree' });
      } else {
        setAiResult(null);
      }
    } catch {
      setAiResult(null);
    } finally {
      setAiSearching(false);
    }
  };

  const runAiActionSearch = async () => {
    const q = normalize(query.slice(1));
    setAiSearching(true);
    setAiResult(null);
    try {
      const plan = await fetchAiActionPlan(selectedYear);
      const items = Array.isArray(plan.actions) ? plan.actions : [];
      const narrowed = q ? items.filter((item) => actionSearchText(item).includes(q)) : items;
      setAiActions(narrowed.slice(0, 6));
    } catch {
      setAiActions([]);
    } finally {
      setAiSearching(false);
    }
  };

  const runAiAction = async (item: AiActionItem) => {
    if (!item.executable) {
      go(
        safeString(item.target?.route || item.route) || '/ai',
        safeString(item.target?.view || item.view) || undefined,
        (item.target?.filters || item.filters || {}) as Record<string, string>,
      );
      return;
    }
    try {
      const result = await executeAiAction(item, { year: selectedYear });
      toast.success(result.message || 'Action executee');
      await invalidateAiTabs(Array.isArray(result.invalidate_tabs) ? result.invalidate_tabs : []);
      setOpen(false);
      setQuery('');
      setAiActions([]);
    } catch (error) {
      toast.error(`Erreur: ${error instanceof Error ? error.message : 'Inconnue'}`);
    }
  };

  const activeItems = isAiActionQuery ? aiActions : navigationItems;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelected((current) => Math.min(current + 1, Math.max(activeItems.length - 1, 0)));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelected((current) => Math.max(current - 1, 0));
      return;
    }
    if (event.key !== 'Enter') return;

    if (isAiActionQuery) {
      if (aiActions[selected]) {
        void runAiAction(aiActions[selected]);
      } else {
        void runAiActionSearch();
      }
      return;
    }
    if (aiResult) {
      go(aiResult.route);
      return;
    }
    if (isAiQuery) {
      void runAiSearch();
      return;
    }
    if (navigationItems[selected]) {
      go(navigationItems[selected].path);
    }
  };

  if (!open) return null;

  return (
    <div className="cmd-backdrop" onClick={() => setOpen(false)}>
      <div className="cmd-palette" onClick={(event) => event.stopPropagation()}>
        <div className="cmd-palette__input-row">
          <span className="cmd-palette__search-icon" aria-hidden="true">⌘K</span>
          <input
            ref={inputRef}
            className="cmd-palette__input"
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Naviguer vers... (? = IA, ! = actions IA)"
          />
        </div>

        <div className="cmd-palette__list" role="listbox">
          {navigationItems.map((item, index) => (
            <button
              key={item.path}
              role="option"
              aria-selected={index === selected}
              className={`cmd-palette__item${index === selected ? ' is-selected' : ''}`}
              onClick={() => go(item.path)}
              onMouseEnter={() => setSelected(index)}
            >
              <span className="cmd-palette__item-icon">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}

          {aiActions.map((item, index) => (
            <button
              key={item.id}
              role="option"
              aria-selected={index === selected}
              className={`cmd-palette__item${index === selected ? ' is-selected' : ''}`}
              onClick={() => {
                void runAiAction(item);
              }}
              onMouseEnter={() => setSelected(index)}
            >
              <span className="cmd-palette__item-icon">⚡</span>
              <span>{item.title}</span>
            </button>
          ))}

          {!aiSearching && !aiResult && navigationItems.length === 0 && aiActions.length === 0 ? (
            <div className="cmd-palette__empty">
              {query.trim()
                ? isAiActionQuery
                  ? 'Appuyez sur Entree pour charger les actions IA'
                  : isAiQuery
                    ? 'Appuyez sur Entree pour rechercher avec l IA'
                    : 'Aucun resultat'
                : 'Aucun resultat'}
            </div>
          ) : null}

          {aiSearching ? <div className="cmd-palette__empty">Recherche IA en cours...</div> : null}

          {aiResult ? (
            <button
              className="cmd-palette__item is-selected"
              onClick={() => go(aiResult.route)}
            >
              <span className="cmd-palette__item-icon">✦</span>
              <span>{aiResult.explanation}</span>
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
